import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { useSearchParam } from 'react-use'
import useSWR from 'swr'

import { GlossaryFeedDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey, useSlug } from '@/lib/contentful/utils'

import { isDevelopment } from '@/utils/isDevelopment'

import { Button } from '@/components/Button'
import { Link } from '@/components/CustomLink'
import { Pagination } from '@/components/Pagination'
import { Warning } from '@/components/Warning'

export const DEFAULT_LIMIT = 10

type Props = {
	className?: string
	ctaText?: string
}
export function GlossaryFeed({ className, ctaText }: Props) {
	const { locale, isPreview } = useRouter()
	const slug = useSlug()
	const pageSearchParam = useSearchParam('page')
	const [page, setPage] = useState(
		typeof pageSearchParam === 'string' ? Number(pageSearchParam) : 1
	)

	const [skip, setSkip] = useState(0)

	useEffect(() => {
		setSkip((page - 1) * DEFAULT_LIMIT)
	}, [page])

	const { data } = useSWR(
		serializeCacheKey('glossaryFeed', {
			category: slug,
			limit: DEFAULT_LIMIT,
			skip,
			locale
		}),
		() =>
			client({ preview: isPreview }).request(GlossaryFeedDocument, {
				where: { category: { slug }, enabled: true },
				limit: DEFAULT_LIMIT,
				skip,
				locale,
				preview: isPreview
			})
	)

	if (!data?.glossaryCollection?.items) {
		if (isDevelopment()) {
			return <Warning>GlossaryFeed: data is undefined</Warning>
		}

		return null
	}

	return (
		<div className={className}>
			<ul className="space-y-8">
				{data.glossaryCollection.items.map((item) => {
					if (!item?.title || !item.slug) return null

					return (
						<li
							key={item.sys.id}
							className="p-8 space-y-4 bg-white border border-gray-1000 rounded-2xl relative"
							data-testId="glossary-feed-item"
						>
							<Link href={`/glossary/${item.slug}`} className="absolute inset-0 z-10 peer">
								<span className="sr-only">{item.title}</span>
							</Link>

							<h2 className="text-xl font-medium text-bc-black">{item.title}</h2>

							<Button variant="subtle" color="secondary" size="large" showIcon fakeButton>
								{ctaText}
							</Button>
						</li>
					)
				})}
			</ul>

			<Pagination
				limit={DEFAULT_LIMIT}
				total={data.glossaryCollection.total}
				className="mt-12 mr-auto ml-0"
				current={page}
				handlePageChange={setPage}
				scrollToTop
			/>
		</div>
	)
}
